import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ChevronDown, PackageCheck, Pencil, ReceiptText, Trash2 } from "lucide-react";
import { api, errMsg } from "../../lib/api";
import { ddmmyyyy, inr, num } from "../../lib/format";
import { useAuth } from "../../context/AuthContext";
import { ConfirmDialog } from "../setup/ConfirmDialog";

const Stat = ({ k, v, cls = "", testid }) => (
  <div className="min-w-0">
    <p className="mono-label text-muted-foreground">{k}</p>
    <p className={`mono truncate font-bold ${cls}`} data-testid={testid}>{v}</p>
  </div>
);

const Line = ({ l, t }) => (
  <div className="space-y-2 border-b py-3 last:border-0" data-testid={t}>
    <div>
      <p className="font-semibold">{l.description} {l.code && <span className="mono text-xs text-muted-foreground">[{l.code}]</span>}</p>
      <p className="text-xs text-muted-foreground">{l.style || "—"} · {l.grn_no} · {num(l.consumption)} {l.uom}/shirt · {inr(l.rate)}/shirt</p>
    </div>
    <div className="grid grid-cols-3 gap-2 rounded-lg bg-card p-3 text-sm">
      <Stat k="Given" v={`${num(l.qty_issued)} ${l.uom}`} />
      <Stat k="Expected" v={num(l.expected_pcs)} />
      <Stat k="Good recd" v={num(l.good_pcs)} cls="text-brand" testid={`${t}-good`} />
      <Stat k="Rejected" v={num(l.rejected_pcs)} cls={l.rejected_pcs ? "text-destructive" : ""} />
      <Stat k="Pending" v={num(l.pending_pcs)} testid={`${t}-pending`} />
      <Stat k="Leftover back" v={`${num(l.leftover_returned)} ${l.uom}`} />
    </div>
    <p className={`mono text-xs font-bold ${l.fabric_balance > 0.001 ? "text-amber-600 dark:text-amber-400" : l.fabric_balance < -0.001 ? "text-destructive" : "text-brand"}`} data-testid={`${t}-balance`}>
      Fabric still with fabricator: {num(l.fabric_balance)} {l.uom}
      {l.fabric_balance < -0.001 && " (more used than given — check per-shirt quantity)"}
    </p>
  </div>
);

const Docs = ({ job, isAdmin, onDelete }) => (
  <>
    {job.receipts.length > 0 && (
      <div>
        <h3 className="mono-label mb-1 text-muted-foreground">Receipts</h3>
        {job.receipts.map((r) => (
          <div key={r.id} className="flex items-center justify-between gap-2 border-b py-2 text-sm last:border-0" data-testid={`receipt-${r.jr_no}`}>
            <span className="min-w-0"><b className="mono">{r.jr_no}</b> · {ddmmyyyy(r.date)} · {r.location}
              <span className="block text-xs text-muted-foreground">{num(r.items.reduce((a, i) => a + i.good_pcs, 0))} good · {num(r.items.reduce((a, i) => a + i.rejected_pcs, 0))} rejected</span></span>
            {isAdmin && <button className="icon-btn hover:!text-destructive" onClick={() => onDelete({ url: `/job-receipts/${r.id}`, label: r.jr_no })} aria-label="Delete receipt" data-testid={`receipt-${r.jr_no}-delete`}><Trash2 size={16} /></button>}
          </div>
        ))}
      </div>
    )}
    {job.bills.length > 0 && (
      <div>
        <h3 className="mono-label mb-1 text-muted-foreground">Fabricator bills</h3>
        {job.bills.map((b) => (
          <div key={b.id} className="flex items-center justify-between gap-2 border-b py-2 text-sm last:border-0" data-testid={`bill-${b.bill_no}`}>
            <span className="min-w-0"><b>Bill {b.bill_no}</b> · {ddmmyyyy(b.bill_date)}
              <span className="mono block text-xs text-muted-foreground">{num(b.items.reduce((a, i) => a + i.pcs, 0))} shirts · {inr(b.taxable)} + {b.gst}% = {inr(b.total)}</span></span>
            {isAdmin && <button className="icon-btn hover:!text-destructive" onClick={() => onDelete({ url: `/job-bills/${b.id}`, label: `bill ${b.bill_no}` })} aria-label="Delete bill" data-testid={`bill-${b.bill_no}-delete`}><Trash2 size={16} /></button>}
          </div>
        ))}
      </div>
    )}
  </>
);

const JobDetail = ({ job, onChanged, t }) => {
  const navigate = useNavigate();
  const { isAdmin, can } = useAuth();
  const [del, setDel] = useState(null);
  const locked = job.receipts.length > 0 || job.bills.length > 0;
  const act = async (fn, msg) => {
    try {
      await fn();
      toast.success(msg);
      onChanged();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <div className="rise space-y-4 border-t bg-muted/20 p-4" data-testid={`${t}-detail`}>
      <p className="text-sm text-muted-foreground">Issued {ddmmyyyy(job.issue_date)} · {job.fabricator.location || "—"}{job.remarks ? ` · ${job.remarks}` : ""}</p>
      <div>{job.items.map((l, i) => <Line key={l.id} l={l} t={`${t}-line-${i}`} />)}</div>
      <Docs job={job} isAdmin={isAdmin} onDelete={setDel} />
      <div className="grid grid-cols-2 gap-2">
        {job.status === "Open" && can("jobs_write") ? <button className="btn btn-accent" onClick={() => navigate(`/jobs/${job.id}/receive`)} data-testid={`${t}-receive-btn`}><PackageCheck size={18} /> Receive</button> : <span />}
        {can("job_bill") && <button className="btn btn-outline" onClick={() => navigate(`/jobs/${job.id}/bill`)} data-testid={`${t}-bill-btn`}><ReceiptText size={18} /> Add bill</button>}
      </div>
      {can("jobs_write") && <div className="grid grid-cols-2 gap-2">
        {!locked ? <button className="btn btn-outline" onClick={() => navigate(`/jobs/${job.id}/edit`)} data-testid={`${t}-edit-btn`}><Pencil size={18} /> Edit</button> : <span />}
        <button className="btn btn-outline" data-testid={`${t}-status-btn`}
          onClick={() => act(() => api.patch(`/jobs/${job.id}/status`, { status: job.status === "Open" ? "Closed" : "Open" }), job.status === "Open" ? "Job closed" : "Job reopened")}>
          {job.status === "Open" ? "Close job" : "Reopen job"}
        </button>
      </div>}
      {isAdmin && <button className="btn btn-outline btn-ghost-danger w-full" onClick={() => setDel({ url: `/jobs/${job.id}`, label: job.job_no })} data-testid={`${t}-delete-btn`}><Trash2 size={18} /> Delete job order</button>}
      <ConfirmDialog open={!!del} onCancel={() => setDel(null)} title={`Delete ${del?.label}?`} body="This cannot be undone. Balances will be recalculated."
        onConfirm={() => { const d = del; setDel(null); act(() => api.delete(d.url), "Deleted"); }} />
    </div>
  );
};

export const JobCard = ({ job, open, onToggle, onChanged }) => {
  const t = `job-card-${job.job_no}`;
  const tt = job.totals;
  return (
    <div className="panel overflow-hidden" data-testid={t}>
      <button type="button" onClick={onToggle} className="flex w-full items-start gap-3 p-4 text-left transition-colors hover:bg-muted/40" data-testid={`${t}-toggle`}>
        <div className="min-w-0 flex-1 space-y-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="mono font-bold">{job.job_no}</span>
            <span className={`chip ${job.status === "Open" ? "chip-Draft" : "chip-Accepted"}`} data-testid={`${t}-status`}>{job.status}</span>
          </div>
          <p className="truncate font-semibold">{job.fabricator.name}</p>
          <p className="text-xs text-muted-foreground">{ddmmyyyy(job.issue_date)} · {job.items.map((l) => l.description).join(", ")}</p>
          <p className="mono text-xs"><b className="text-brand">{num(tt.good_pcs)}</b>/{num(tt.expected_pcs)} shirts · pending {num(tt.pending_pcs)} · rejected {num(tt.rejected_pcs)} · leftover {num(tt.leftover_returned)}</p>
          <p className="mono text-xs text-muted-foreground" data-testid={`${t}-costing`}>Fabric {inr(tt.fabric_cost)} + job work {inr(tt.conversion_cost)} = {inr(tt.total_cost)}{tt.good_pcs ? ` · ${inr(tt.cost_per_pc)}/shirt` : ""}</p>
        </div>
        <ChevronDown size={18} className={`mt-1 shrink-0 text-muted-foreground transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && <JobDetail job={job} onChanged={onChanged} t={t} />}
    </div>
  );
};
